import { z } from 'zod'

// Zod schemas
const planDocumentSchema = z.object({
  type: z.string(),
  url: z.string(),
})

const supplierRatingsSchema = z.object({
  customerService: z.number(),
  onlineAccessibility: z.number(),
  businessHistory: z.number(),
  overallScore: z.number(),
  planOptions: z.number(),
})

const supplierControlsSchema = z.object({
  disableGrid: z.boolean(),
  disableCart: z.boolean(),
  disablePhoneNumbers: z.boolean(),
  disableCartLinks: z.boolean(),
  disablePhone: z.boolean(),
})

const supplierSchema = z.object({
  id: z.string(),
  isPartner: z.boolean(),
  logo: z.string(),
  name: z.string(),
  parentId: z.string(),
  phone: z.string(),
  whatToKnow: z.string().nullable(),
  creditThreshold: z.string(),
  ratings: supplierRatingsSchema,
  controls: supplierControlsSchema,
})

const planRateSchema = z.object({
  price: z.number(),
  type: z.string(),
})

const planTermSchema = z.object({
  length: z.number().nullable(),
  type: z.string(),
})

const planFeeSchema = z.object({
  type: z.string(),
  amount: z.number(),
  monthly: z.boolean(),
})

const planBadgeSchema = z.object({
  id: z.string(),
  name: z.string(),
  tooltip: z.string(),
  source: z.string(),
  attributeValue: z.string(),
})

const harbingerIdentifiersSchema = z.object({
  pointInTimeId: z.number(),
  lifetimeId: z.string(),
})

const billCreditSchema = z.object({
  amount: z.number(),
  startKwh: z.number(),
  endKwh: z.number(),
})

const incentiveFullSchema = z.object({
  dwellingType: z.string(),
  intent: z.string(),
  specialOfferText: z.string(),
  disclaimer: z.string(),
})

// Main plan schema
export const planSchema = z.object({
  id: z.string(),
  description: z.string(),
  priceToComparePercentage: z.number().nullable(),
  secondaryDescription: z.string(),
  pricePerKwh: z.number().nullable(),
  documents: z.array(planDocumentSchema),
  name: z.string(),
  unitOfMeasure: z.string(),
  supplier: supplierSchema,
  incentivesFull: z.union([incentiveFullSchema, z.array(incentiveFullSchema)]).nullable(),
  rates: z.array(planRateSchema),
  term: planTermSchema,
  type: z.string(),
  fees: z.array(planFeeSchema),
  features: z.array(z.string()),
  cartActive: z.boolean(),
  cartLink: z.string(),
  badges: z.array(planBadgeSchema),
  phoneActive: z.boolean(),
  active: z.boolean(),
  hideOnGrid: z.boolean(),
  utility: z.object({
    id: z.string(),
    name: z.string(),
  }),
  renewablePercentage: z.number(),
  harbingerIdentifiers: harbingerIdentifiersSchema,
  billCredits: z.array(billCreditSchema).nullable(),
  isFeatured: z.boolean().optional(),
  featuredIndex: z.number().optional(),
  // The plan the shopper was viewing before the FMP guided path, pinned to featured slot 1
  isPreviouslyViewed: z.boolean().optional(),
  labelId: z.string().nullable().optional(),
  // Monarch Grid Featured Config text that replaces the PowerAdmin badge copy for this plan
  featuredLabelOverride: z.string().optional(),
  usageId: z.string().nullable().optional(),
  annualSavings: z.number().optional(),
  bestValueLabel: z.string().optional(),
  bestValueHomeSize: z.string().optional(),
  manualKwhPrices: z
    .record(
      z.number(),
      z.object({
        pricePerKwh: z.number(),
      }),
    )
    .optional()
    .nullable(),
  eflData: z
    .object({
      billCredits: z.string().optional(),
    })
    .optional(),
})

// Infer TypeScript types from schemas
export type PlanDocument = z.infer<typeof planDocumentSchema>
export type PlanRate = z.infer<typeof planRateSchema>
export type PlanFee = z.infer<typeof planFeeSchema>
export type PlanBadge = z.infer<typeof planBadgeSchema>
export type Plan = z.infer<typeof planSchema>
